import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk, createAppAsyncThunk } from "appThunk";
import { ViewportProps } from "react-map-gl";

import {
    selectComparatorsByChapter,
    selectPinnedLocation,
    selectHasErrors as selectLocationHasErrors,
    selectIsLoading as selectLocationIsLoading,
    selectPlaceCategories,
    selectSizeBuckets,
    selectTarget,
} from "modules/customer/tools/location/locationSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";
import { loadNearbyStores, NearbyStore } from "./nearbyStore";
import mathUtils from "utils/mathUtils";
import { numberSortExpression, SortDirection, stringSortExpression } from "utils/sortUtils";
import _ from "lodash";
import { max, min, mode } from "mathjs";
import { SizeBucket } from "modules/customer/tools/location/sizeBucket";
import { BrandPosition } from "./brandPosition";
import { BrandClassification } from "domain/BrandClassification";
import { PlaceSuperCategory } from "./placeSuperCategory";
import { StoreSize } from "./storeSize";
import { RagIndicator, RagIndicatorStatus } from "domain/ragIndicator";
import { ScoreField } from "modules/customer/tools/location/retailCentre";

export type RetailComplexionSeries = "Pinned Location" | "Comparator";

interface LoadRetailersResponse {
    nearbyStores: NearbyStore[],
    brandPositions: BrandPosition[],
    placeSuperCategories: PlaceSuperCategory[],
    storeSizes: StoreSize[]
}

interface RetailersState {
    isLoading: boolean,
    hasErrors: boolean,
    brandPositions: BrandPosition[],
    nearbyStores: NearbyStore[],
    placeSuperCategories: PlaceSuperCategory[],
    retailComplexionSeries: RetailComplexionSeries,
    storeSizes: StoreSize[],
    viewport?: ViewportProps;
}

const initialState: RetailersState = {
    isLoading: false,
    hasErrors: false,
    brandPositions: [],
    nearbyStores: [],
    placeSuperCategories: [],
    retailComplexionSeries: "Pinned Location",
    storeSizes: [],
    viewport: undefined
};

const retailersSlice = createSlice({
    name: "customer/tools/location/retailers",
    initialState,
    reducers: {
        chooseAllBrandPositions: (state) => {
            state.brandPositions.forEach(item => item.setIsSelected(true));
        },
        chooseAllPlaceSuperCategories: (state) => {
            state.placeSuperCategories.forEach(item => item.setIsSelected(true));
        },
        chooseAllStoreSizes: (state) => {
            state.storeSizes.forEach(item => item.setIsSelected(true));
        },
        deselectAllBrandPositions: (state) => {
            state.brandPositions.forEach(item => item.setIsSelected(false));
        },
        deselectAllPlaceSuperCategories: (state) => {
            state.placeSuperCategories.forEach(item => item.setIsSelected(false));
        },
        deselectAllStoreSizes: (state) => {
            state.storeSizes.forEach(item => item.setIsSelected(false));
        },
        setRetailComplexionSeries: (state, action: PayloadAction<RetailComplexionSeries>) => {
            state.retailComplexionSeries = action.payload;
        },
        clearBrandPositions: (state) => {
            state.brandPositions = initialState.brandPositions;
        },
        clearNearbyStores: (state) => {
            state.nearbyStores = initialState.nearbyStores;
        },
        clearPlaceSuperCategories: (state) => {
            state.placeSuperCategories = initialState.placeSuperCategories;
        },
        clearStoreSizes: (state) => {
            state.storeSizes = initialState.storeSizes;
        },
        resetRetailComplexionSeries: (state) => {
            state.retailComplexionSeries = initialState.retailComplexionSeries;
        },
        toggleBrandPositionIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.brandPositions.find(item => item.id === id)?.toggleIsSelected();
        },
        togglePlaceSuperCategoryIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.placeSuperCategories.find(item => item.id === id)?.toggleIsSelected();
        },
        toggleStoreSizeIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.storeSizes.find(item => item.id === id)?.toggleIsSelected();
        },
        setViewport: (state, action: PayloadAction<ViewportProps>) => {
            state.viewport = action.payload;
        },
        clearViewport: (state) => {
            state.viewport = initialState.viewport;
        },
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadRetailers.pending, (state: RetailersState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.brandPositions = initialState.brandPositions;
            state.nearbyStores = initialState.nearbyStores;
            state.placeSuperCategories = initialState.placeSuperCategories;
            state.storeSizes = initialState.storeSizes;
        });
        builder.addCase(loadRetailers.rejected, (state: RetailersState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.brandPositions = initialState.brandPositions;
            state.nearbyStores = initialState.nearbyStores;
            state.placeSuperCategories = initialState.placeSuperCategories;
            state.storeSizes = initialState.storeSizes;
        });
        builder.addCase(loadRetailers.fulfilled, (state: RetailersState, action: PayloadAction<LoadRetailersResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.brandPositions = action.payload.brandPositions;
            state.nearbyStores = action.payload.nearbyStores;
            state.placeSuperCategories = action.payload.placeSuperCategories;
            state.storeSizes = action.payload.storeSizes;
        });
    }
});

export const {
    chooseAllBrandPositions,
    chooseAllPlaceSuperCategories,
    chooseAllStoreSizes,
    deselectAllBrandPositions,
    deselectAllPlaceSuperCategories,
    deselectAllStoreSizes,
    toggleBrandPositionIsSelected,
    togglePlaceSuperCategoryIsSelected,
    toggleStoreSizeIsSelected,
    setRetailComplexionSeries,
    setViewport
} = retailersSlice.actions;

export const loadRetailers = createAppAsyncThunk(
    "customer/tools/location/retailers/loadRetailers",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const placeCategories = selectPlaceCategories(state);
            const sizeBuckets = selectSizeBuckets(state);
            const pinnedLocation = selectPinnedLocation(state);
            const comparatorStore = selectComparatorsByChapter(state)?.retailers;
            const retailCentreIds = [];
            if (pinnedLocation) {
                retailCentreIds.push(pinnedLocation.retailCentre.id);
            }
            if (comparatorStore) {
                retailCentreIds.push(comparatorStore.retailCentreId);
            }
            const nearbyStores = await thunkAPI.dispatch(loadNearbyStores(retailCentreIds, placeCategories, sizeBuckets));

            const brandPositions = BrandClassification.getAll().map(classification => new BrandPosition(
                classification.name,
                true
            ));

            const placeSuperCategories = _([...placeCategories])
                .filter(category => category.isRetailer)
                .groupBy(category => category.superCategoryName)
                .map((collection, superCategoryName) => new PlaceSuperCategory(
                    superCategoryName,
                    collection.map(item => item.id),
                    true
                ))
                .sort((a, b) => stringSortExpression(a.name, b.name, SortDirection.ASC))
                .value();

            const storeSizes: StoreSize[] = [];
            const unknownStoreSize = new StoreSize(
                "Unknown",
                true
            );
            storeSizes.push(unknownStoreSize);
            const sizeBucketLabels = new Set(sizeBuckets.map(size => size.label));

            sizeBucketLabels.forEach(sizeLabel => {
                storeSizes.push(new StoreSize(
                    sizeLabel,
                    true
                ));
            });

            const loadRetailersResponse: LoadRetailersResponse = {
                nearbyStores,
                brandPositions,
                placeSuperCategories,
                storeSizes
            };
            return loadRetailersResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Retailers.", error));
            return thunkAPI.rejectWithValue([]);
        }
    }
);

export const clearRetailers = (): AppThunk => (dispatch) => {
    dispatch(retailersSlice.actions.clearBrandPositions());
    dispatch(retailersSlice.actions.clearNearbyStores());
    dispatch(retailersSlice.actions.clearPlaceSuperCategories());
    dispatch(retailersSlice.actions.clearStoreSizes());
    dispatch(retailersSlice.actions.resetRetailComplexionSeries());
    dispatch(retailersSlice.actions.clearViewport());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.tools.location.retailers.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.tools.location.retailers.hasErrors;
};

export const selectBrandPositions = (state: RootState) => {
    return state.customer.tools.location.retailers.brandPositions;
};

export const selectNearbyStores = (state: RootState) => {
    return state.customer.tools.location.retailers.nearbyStores;
};

export const selectPlaceSuperCategories = (state: RootState) => {
    return state.customer.tools.location.retailers.placeSuperCategories;
};

export const selectRetailComplexionSeries = (state: RootState) => {
    return state.customer.tools.location.retailers.retailComplexionSeries;
};

export const selectStoreSizes = (state: RootState) => {
    return state.customer.tools.location.retailers.storeSizes;
};

export const selectViewport = (state: RootState) => {
    return state.customer.tools.location.retailers.viewport;
};

export const selectRetailersAlignmentStatus = createSelector(
    (state: RootState) => selectLocationIsLoading(state),
    (state: RootState) => selectLocationHasErrors(state),
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectTarget(state),
    (isLoading, hasErrors, pinnedLocation, target) => {
        const id = "retailers-alignment";
        let label = "Number of stores & type of retailers in the selected location";
        let status = RagIndicatorStatus.Info;
        if (isLoading || hasErrors) {
            return new RagIndicator(id, status, label, "", isLoading, hasErrors);
        }
        if (!pinnedLocation) {
            return new RagIndicator(id, status, label, "No location selected.");
        }
        if (!target?.useRetailers) {
            return new RagIndicator(id, RagIndicatorStatus.NoData, "No target set for Retailers", "");
        }
        const score = pinnedLocation.retailCentre.getRagScore(ScoreField.Retailers);
        switch (score) {
            case 5:
            case 4:
                status = RagIndicatorStatus.Green;
                label = "The number of stores and type of retailers in the selected location align strongly with your set targets.";
                break;
            case 3:
            case 2:
                status = RagIndicatorStatus.Amber;
                label = "The number of stores and type of retailers in the selected location align moderately with your set targets.";
                break;
            default:
                status = RagIndicatorStatus.Red;
                label = "The number of stores and type of retailers in the selected location align weakly with your set targets.";
        }
        return new RagIndicator(id, status, label, "");
    }
);

export const selectRetailersAlignmentScore = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (pinnedLocation) => {
        return pinnedLocation?.retailCentre.retailersScore ?? 0;
    }
);

export const selectRetailZoneCharacteristics = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectTarget(state),
    (pinnedLocation, target) => {
        const retailCentre = pinnedLocation?.retailCentre;
        return {
            retailCentre: {
                numberOfStores: retailCentre?.numberOfStoresCentile ?? 0,
                brandPositioning: retailCentre?.nullBrandPositioning ? null : (retailCentre?.brandPositioningCentile ?? 0)
            },
            target: {
                numberOfStores: target?.numberOfStores ?? 0,
                brandPositioning: target?.brandPositioning ?? 0
            }
        };
    }
);

export const selectFilteredNearbyStores = createSelector(
    selectNearbyStores,
    selectBrandPositions,
    selectPlaceSuperCategories,
    selectStoreSizes,
    (nearbyStores, brandPositions, placeSuperCategories, storeSizes) => {
        const selectedBrandPositions = brandPositions.filter(item => item.isSelected()).map(item => item.name);
        const selectedPlaceSuperCategories = placeSuperCategories.filter(item => item.isSelected()).map(item => item.name);
        const selectedStoreSizes = storeSizes.filter(item => item.isSelected()).map(item => item.name);
        return nearbyStores.filter(store =>
            selectedBrandPositions.includes(store.brandClassification.name)
            && selectedPlaceSuperCategories.includes(store.placeSuperCategory)
            && selectedStoreSizes.includes(store.sizeBucketName)
        );
    }
);

export const selectPinnedLocationNearbyStores = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    selectFilteredNearbyStores,
    (pinnedLocation, nearbyStores) => {
        return nearbyStores
            .filter(store => store.retailCentreId === pinnedLocation?.retailCentre.id)
            // For pins to display on map in correct order
            .sort((a, b) => numberSortExpression(a.brandClassification.priority, b.brandClassification.priority, SortDirection.ASC));
    }
);

interface KpiComparison {
    pinnedLocationValue?: number
    comparisonValue?: number
    difference?: number
}

export const selectNearbyStoresDownloadData = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectPlaceCategories(state),
    (state: RootState) => selectSizeBuckets(state),
    selectPinnedLocationNearbyStores,
    (pinnedLocation, placeCategories, sizeBuckets, nearbyStores) => {
        if (nearbyStores.length === 0) {
            return [];
        }
        return nearbyStores
            .map(competitor => {
                const distanceToSelectedLocation = mathUtils.haversineDistance(
                    pinnedLocation?.latitude ?? 0,
                    pinnedLocation?.longitude ?? 0,
                    competitor?.latitude ?? 0,
                    competitor?.longitude ?? 0
                );
                const placeCategory = placeCategories.find(category => category.id === competitor.placeCategoryId);
                const placeCategoryName = placeCategory?.name ?? "Unknown";
                const sizeBucket = sizeBuckets.find(item => item.id === competitor.sizeBucketId);

                return {
                    ...competitor,
                    distanceToSelectedLocation,
                    placeCategoryName,
                    sizeBucket
                };
            })
            .sort((a, b) => numberSortExpression(a.distanceToSelectedLocation, b.distanceToSelectedLocation, SortDirection.ASC));
    }
);


export const selectStoreCounts = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    selectFilteredNearbyStores,
    (pinnedLocation, comparators, nearbyStores) => {
        if (nearbyStores.length === 0) {
            return {};
        }

        const numberOfStoresPerRetailCentre = _(nearbyStores)
            .groupBy(store => store.retailCentreId)
            .map((group, retailCentreId) => {
                return {
                    retailCentreId: Number(retailCentreId),
                    numberOfStores: group.length,
                    independenceRate: 100 * (group.filter(store => store.isIndependent).length / group.length)
                };
            })
            .value();

        const selectedStoreValues = numberOfStoresPerRetailCentre.find(item => item.retailCentreId === pinnedLocation?.retailCentre?.id);
        const comparatorValues = numberOfStoresPerRetailCentre.find(item => item.retailCentreId === comparators?.retailers?.retailCentreId);
        const selectedCount = selectedStoreValues?.numberOfStores ?? 0;
        const comparatorCount = comparatorValues?.numberOfStores ?? 0;

        const numberOfStores: KpiComparison = {
            pinnedLocationValue: selectedCount,
            comparisonValue: comparatorCount,
            difference: mathUtils.safePercentageChange(selectedCount, comparatorCount)
        };

        const selectedIndepenceRate = selectedStoreValues?.independenceRate ?? 0;
        const comparatorIndependenceRate = comparatorValues?.independenceRate ?? 0;
        const independenceRate: KpiComparison = {
            pinnedLocationValue: selectedIndepenceRate,
            comparisonValue: comparatorIndependenceRate,
            difference: selectedIndepenceRate - comparatorIndependenceRate
        };

        return {
            numberOfStores,
            independenceRate
        };
    }
);

export const selectGroupedSizeBuckets = createSelector(
    (state: RootState) => selectSizeBuckets(state),
    (sizeBuckets) => {
        if (sizeBuckets.length === 0) {
            return [];
        }

        let id = 0;
        const groupedSizeBuckets = _([...sizeBuckets])
            .sort((a, b) => numberSortExpression(a.id, b.id, SortDirection.ASC))
            .groupBy(sizeBucket => sizeBucket.label)
            .map((group, label) => {
                const lowerBound = min(group.map(sizeBucket => sizeBucket.lowerBound));
                const upperBounds = group.map(sizeBucket => sizeBucket.upperBound);
                const upperBound = upperBounds.includes(null) ? null : max(upperBounds as number[]);
                return new SizeBucket(
                    id++,
                    lowerBound,
                    upperBound,
                    label
                );
            })
            .value();

        return groupedSizeBuckets;
    }
);

export const selectMostCommonStoreSize = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectGroupedSizeBuckets,
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    (state: RootState) => selectSizeBuckets(state),
    (isLoading, hasErrors, nearbyStores, groupedSizeBuckets, pinnedLocation, comparators, sizeBuckets) => {
        const mostCommonStoreSize: { pinnedLocation: SizeBucket | undefined, comparator: SizeBucket | undefined } = {
            pinnedLocation: undefined,
            comparator: undefined
        };
        if (isLoading || hasErrors || nearbyStores.length === 0 || !pinnedLocation || !comparators) {
            return mostCommonStoreSize;
        }
        const filteredStores = nearbyStores.filter(store => store.sizeBucketName !== "Unknown");

        const groupedSizeBucketsByOriginalSizeBucketId = new Map<number, SizeBucket | undefined>(
            sizeBuckets.map(sizeBucket => {
                return [sizeBucket.id, groupedSizeBuckets.find(groupedSizeBucket => groupedSizeBucket.label === sizeBucket.label)];
            })
        );

        const mostCommonStoreSizeByRetailCentreId = _(filteredStores)
            .groupBy(store => store.retailCentreId)
            .map((group, retailCentreId) => {
                const groupedSizeBuckets = group.map(item => groupedSizeBucketsByOriginalSizeBucketId.get(item.sizeBucketId)).filter(item => item !== undefined) as SizeBucket[];
                const modeValues = groupedSizeBuckets.length > 0 ? mode(groupedSizeBuckets.map(item => item.id)) as number[] : [];
                const smallestOfModes = modeValues.length > 0 ? modeValues.sort((a, b) => numberSortExpression(a, b, SortDirection.ASC))[0] : null;
                return {
                    retailCentreId: Number(retailCentreId),
                    mostCommonStoreSizeId: smallestOfModes
                };
            })
            .value();

        const pinnedLocationStoreSizeId = mostCommonStoreSizeByRetailCentreId.find(item => item.retailCentreId === pinnedLocation.retailCentre.id)?.mostCommonStoreSizeId;
        mostCommonStoreSize.pinnedLocation = groupedSizeBuckets.find(item => item.id === pinnedLocationStoreSizeId);
        const comparatorStoreSizeId = mostCommonStoreSizeByRetailCentreId.find(item => item.retailCentreId === comparators.retailers.retailCentreId)?.mostCommonStoreSizeId;
        mostCommonStoreSize.comparator = groupedSizeBuckets.find(item => item.id === comparatorStoreSizeId);

        return mostCommonStoreSize;
    }
);

export const selectNumberOfStoresPerCategory = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    (state: RootState) => selectPlaceCategories(state),
    (isLoading, hasErrors, nearbyStores, pinnedLocation, comparators, placeCategories) => {
        if (isLoading || hasErrors || nearbyStores.length === 0 || !pinnedLocation || !comparators) {
            return [];
        }

        const storeCountsByCategory = _(nearbyStores)
            .groupBy(store => store.placeCategoryId)
            .map((groupByCategory, categoryId) => {
                const countsByStore = _(groupByCategory)
                    .groupBy(store => store.retailCentreId)
                    .map((groupByStore, retailCentreId) => ({
                        retailCentreId: Number(retailCentreId),
                        numberOfStores: groupByStore.length
                    }))
                    .value();

                return {
                    categoryId: Number(categoryId),
                    countsByStore
                };
            });

        const allRelevantPlaceCategoryIds = nearbyStores.map(store => store.placeCategoryId);
        const uniquePlaceCategoryIds = Array.from(new Set(allRelevantPlaceCategoryIds));
        const relevantPlaceCategories = placeCategories.filter(item => uniquePlaceCategoryIds.includes(item.id));

        const storeCountsAllCats = relevantPlaceCategories.map(category => {
            const storeCounts = {
                category,
                selectedLocation: 0,
                comparator: 0
            };
            const counts = storeCountsByCategory.find(count => count.categoryId === category.id)?.countsByStore;
            if (!counts) {
                return storeCounts;
            }
            storeCounts.selectedLocation = counts.find(item => item.retailCentreId === pinnedLocation.retailCentre.id)?.numberOfStores ?? 0;
            storeCounts.comparator = counts.find(item => item.retailCentreId === comparators.retailers.retailCentreId)?.numberOfStores ?? 0;

            return storeCounts;
        });

        const storeCountsBySuperCategories = _(storeCountsAllCats)
            .groupBy(item => item.category.superCategoryName)
            .map((group, placeSuperCategory) => {
                return {
                    placeSuperCategory,
                    selectedStoreTotalStoreCount: group.reduce((total, current) => total + current.selectedLocation, 0),
                    comparatorTotalStoreCount: group.reduce((total, current) => total + current.comparator, 0),
                    storeCountByPlaceCategory: group.map(item => ({
                        placeCategory: item.category,
                        selectedLocationStoreCount: item.selectedLocation,
                        comparatorStoreCount: item.comparator
                    })).sort((a, b) => numberSortExpression(a.selectedLocationStoreCount, b.selectedLocationStoreCount, SortDirection.DESC))
                };
            })
            .value()
            .sort((a, b) => numberSortExpression(a.selectedStoreTotalStoreCount, b.selectedStoreTotalStoreCount, SortDirection.DESC));
        return storeCountsBySuperCategories;
    }
);

export enum RetailComplexionLevel {
    BrandClassification = "Brand Classification",
    BrandName = "Brand Name"
}

export interface RetailComplexionTreemapRow {
    name: string,
    type: RetailComplexionLevel,
    colourId: number,
    parent: string | undefined,
    numberOfStores: number,
    percentageOfLevel: number
}

export const selectRetailComplexion = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    selectIsLoading,
    selectHasErrors,
    selectFilteredNearbyStores,
    selectRetailComplexionSeries,
    (pinnedLocation, comparators, isLoading, hasErrors, nearbyStores, retailComplexionSeries) => {
        if (!pinnedLocation || !comparators || isLoading || hasErrors || nearbyStores.length === 0) {
            return [];
        }

        const filteredStores = retailComplexionSeries === "Pinned Location"
            ? nearbyStores.filter(store => store.retailCentreId === pinnedLocation.retailCentre.id)
            : nearbyStores.filter(store => store.retailCentreId === comparators.retailers.retailCentreId);

        const totalNumberofStores = filteredStores.length;
        const brandClassifications = _(filteredStores)
            .groupBy(item => item.brandClassification.name)
            .map((group, classificationName) => {
                const numberOfStores = group.length;
                const aggregatedBrandClassifications: RetailComplexionTreemapRow = {
                    name: classificationName,
                    colourId: group[0].brandClassification.colourId,
                    type: RetailComplexionLevel.BrandClassification,
                    parent: undefined,
                    numberOfStores,
                    percentageOfLevel: 100 * (numberOfStores / totalNumberofStores)
                };
                return aggregatedBrandClassifications;
            }).value();

        const brandNames = _(filteredStores)
            .groupBy(item => item.brandName)
            .map((group, name) => {
                const numberOfStores = group.length;
                const parentName = group[0].brandClassification.name;
                const numberOfStoresInParent = brandClassifications.find(item => item.name === parentName)?.numberOfStores ?? numberOfStores;
                const aggregatedBrandNames: RetailComplexionTreemapRow = {
                    name: name === "null" ? "Independents" : name,
                    colourId: 0,
                    type: RetailComplexionLevel.BrandName,
                    parent: parentName,
                    numberOfStores,
                    percentageOfLevel: 100 * (numberOfStores / numberOfStoresInParent)
                };
                return aggregatedBrandNames;
            }).value();

        return [...brandClassifications, ...brandNames];
    }
);

export default retailersSlice;
